import React, { useEffect, useState } from "react";
import { useStores } from "../../../use-stores";
import { observer } from "mobx-react";
import { Box, Button, useTheme, Grid2 } from "@mui/material";
import CustomTypography from "../../components/common/form/CustomTypography";
import { DateRangePickerNew } from "../../components/common/textFields/DatePickerRangeNew";
import dayjs, { Dayjs } from "dayjs";
import { useSearchParams } from "react-router-dom";
import {
  parseQueryParamsForDashboardFilters,
  DashboardFilters,
  DashboardSelectedFilters,
} from "../../../models/Dashboard/DashboardFilters";

import AppDropDown from "../../components/common/filter/AppDropDown";
import AppButton from "../../components/common/buttons/AppButton";
import { useNotification } from "../../../utils/NotificationsContext";
import LoaderOverlay from "../../components/common/pages/AppLoader";
import PermissionBlockLabel from "../../components/common/pop-up/PermissionBlockLabel ";
import { EmployeeRoles } from "../../../models/Employees/EmployeeRoles";
import { SortType } from "../../../models/SortType";
import { FilterValue } from "../../../models/Filters/FilterValue";
import TypeFilterCard from "../../components/filters/TypeFilterCard";
import DatePicker2 from "../../components/common/textFields/DatePicker2";
import { Admin } from "../../../models/TaskTracker/Admin";
import AppSearchDropDown2 from "../../components/common/filter/AppSearchDropdown2";

const DASHBOARD_FILTERS_KEY = "DASHBOARD_FILTERS_KEY";

const Dashboard: React.FC = () => {
  const theme = useTheme();

  const { dashboardAnalyticsStore, userStore, TaskTrackerStore } = useStores();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedFiltersData, setSelectedFiltersData] = useState<FilterValue[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const { showError } = useNotification();
  let apiTimer: any;

  const initialFilters: DashboardFilters = parseQueryParamsForDashboardFilters(
    localStorage.getItem(DASHBOARD_FILTERS_KEY)
      ? JSON.parse(localStorage.getItem(DASHBOARD_FILTERS_KEY)!)
      : null,
    searchParams
  );

  const [filters, setFilters] = useState<DashboardFilters>(initialFilters);
  const [selectedFilters, setSelectedFilters] =
    useState<DashboardSelectedFilters>({
      ...initialFilters,
      start_date: initialFilters.start_date.format("YYYY-MM-DD"),
      end_date: initialFilters.end_date.format("YYYY-MM-DD"),
      date: initialFilters.date.format("YYYY-MM-DD"),
    });

  useEffect(() => {
    updateSelectedFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    handleApply();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const newSearchParams = new URLSearchParams();
    Object.entries(selectedFilters).forEach(([key, value]) => {
      if (value) {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            newSearchParams.set(key, value.map((item) => item.id).join(","));
          }
        } else {
          newSearchParams.set(key, value);
        }
      }
    });

    setSearchParams(newSearchParams);
  }, [selectedFilters]);

  const updateSelectedFilters = () => {
    localStorage.setItem(DASHBOARD_FILTERS_KEY, JSON.stringify(filters));

    const start_date = filters.start_date.format("YYYY-MM-DD");
    const end_date = filters.end_date.format("YYYY-MM-DD");
    const date = filters.date.format("YYYY-MM-DD");

    setSelectedFilters((_) => ({
      ...filters,
      start_date: start_date,
      end_date: end_date,
      date: date,
    }));

    const newFilters: React.SetStateAction<FilterValue[]> = [];

    if (filters.poc_ids) {
      filters.poc_ids.forEach((poc_ids) =>
        newFilters.push({ id: "poc_ids", value: poc_ids.value })
      );
    }

    setSelectedFiltersData(newFilters);
  };

  const handleAdminSearch = (searchText: string) => {
    if (apiTimer) clearTimeout(apiTimer);
    if (searchText.length > 2) {
      apiTimer = setTimeout(() => {
        TaskTrackerStore.getAdminBySearch(searchText).catch((err) => {
          showError("Error", err?.response?.data?.message);
        });
      }, 1500);
    }
  };

  const handleApply = async () => {
    setLoading(true);

    try {
      setFilters((prev) => ({
        ...prev,
        page: 1,
        limit: 30,
        search: "",
        sortBy: "created_date",
        sortType: SortType.DESC,
      }));

      await dashboardAnalyticsStore.getChurnTats({
        poc_ids: selectedFilters.poc_ids,
        start_date: selectedFilters.start_date,
        end_date: selectedFilters.end_date,
        date: selectedFilters.date,
      });

      // await opsAnalyticsDashboardStore.getAllAnalyticsData({
      //   pm_adminids: selectedFilters.pm_adminids,
      //   archived: selectedFilters.archived,
      //   order_id: selectedFilters.order_id,
      //   order_type: selectedFilters.order_type,
      //   start_date: selectedFilters.start_date,
      //   end_date: selectedFilters.end_date,
      //   excluded_orders:
      //     selectedFilters.excluded_orders?.id === "true" ? true : false,
      // });

      // await opsAnalyticsDashboardStore.getOrderListTableData({
      //   pm_adminids: selectedFilters.pm_adminids,
      //   archived: selectedFilters.archived,
      //   order_id: selectedFilters.order_id,
      //   order_type: selectedFilters.order_type,
      //   start_date: selectedFilters.start_date,
      //   end_date: selectedFilters.end_date,
      //   limit: 30,
      //   search: "",
      //   page: 1,
      //   sortBy: "created_date",
      //   sortType: SortType.DESC,
      //   excluded_orders:
      //     selectedFilters.excluded_orders?.id === "true" ? true : false,
      // });
    } catch (error) {
      showError("Error", "Failed to fetch the analytics dashboard details 2.");
    } finally {
      setTimeout(() => setLoading(false), 500);
    }
  };

  const handleRangeSelect = (range: {
    startDate: Dayjs | null;
    endDate: Dayjs | null;
  }) => {
    if (range.startDate && range.endDate) {
      setFilters((prev) => ({
        ...prev,
        start_date: range.startDate!,
        end_date: range.endDate!,
        date: filters.date!,
      }));
    }
  };

  const handleRemoveFilter = (index: number) => {
    const filterToRemove = selectedFiltersData[index];
    const updatedFilters = selectedFiltersData.filter((_, i) => i !== index);
    setSelectedFiltersData(updatedFilters);

    switch (filterToRemove.id) {
      case "poc_ids":
        setFilters((prev) => ({
          ...prev,
          poc_ids:
            filters.poc_ids?.filter(
              (item) => item.value !== filterToRemove.value
            ) || [],
        }));
        break;
      default:
        break;
    }
  };

  const handleClearAllFilters = () => {
    setFilters((_) => ({
      ...initialFilters,
      poc_ids: [],
    }));

    setSelectedFiltersData([]);
  };

  return (
    <>
      {userStore.user && (
        <>
          {userStore.hasPermission(EmployeeRoles.VIEW_DASHBOARD_ANALYTICS) ? (
            <>
              <LoaderOverlay loading={loading} />
              <Box
                sx={{
                  margin: "8px",
                  gap: 2,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    backgroundColor: theme.palette.customColors.white,
                    padding: 4,
                    borderRadius: 2,
                    gap: 2,
                  }}
                >
                  <Grid2
                    container
                    spacing={2}
                    columns={12}
                    sx={{ width: "100%" }}
                  >
                    <Grid2 size={{ lg: 3, md: 4, sm: 12, xs: 12 }}>
                      <CustomTypography
                        variant="heading8"
                        color={theme.palette.customColors.text}
                        style={{ marginBottom: 6 }}
                      >
                        Date
                      </CustomTypography>
                      <DatePicker2
                        label=""
                        type="date"
                        onChange={(date) => {
                          setFilters((prev) => ({
                            ...prev,
                            date: date!,
                          }));
                        }}
                        value={filters.date}
                        maxDate={dayjs().startOf("day")}
                      />
                      {/* <CustomTypography
                        variant="heading8"
                        color={theme.palette.customColors.text}
                        style={{ marginBottom: 6 }}
                      >
                        Date Range
                      </CustomTypography>
                      <DateRangePickerNew
                        onRangeSelect={handleRangeSelect}
                        startDate={filters.start_date}
                        endDate={filters.end_date}
                      />*/}
                    </Grid2>
                    <Grid2 size={{ lg: 3, md: 4, sm: 12, xs: 12 }}>
                      <CustomTypography
                        variant="heading7"
                        color={theme.palette.customColors.tableGrayHeadingColor}
                        style={{ marginBottom: 10, fontSize: "0.8rem" }}
                      >
                        {"Sales POC"}
                      </CustomTypography>
                      <AppSearchDropDown2
                        showSelection={false}
                        isMultiSelect={true}
                        title="Select Managers"
                        fontSize="0.9rem"
                        value={filters.poc_ids}
                        list={
                          TaskTrackerStore?.admins?.map((admin: Admin) => ({
                            id: admin.id.toString(),
                            value: admin.name,
                          })) || []
                        }
                        local={false}
                        handleSearch={(poc_ids) => handleAdminSearch(poc_ids!)}
                        handleSelect={(items) => {
                          setFilters((prev) => ({
                            ...prev,
                            poc_ids: items,
                          }));
                        }}
                      />
                    </Grid2>
                    <Grid2 size={{ lg: 3, md: 4, sm: 12, xs: 12 }}></Grid2>
                    <Grid2
                      size={{ lg: 3, md: 4, sm: 12, xs: 12 }}
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "flex-end",
                        gap: 2,
                      }}
                    >
                      <AppButton
                        variant="primaryBlock"
                        label="Search"
                        onClick={handleApply}
                        customStyles={{ width: "100%" }}
                        disabled={loading}
                      />
                      <AppButton
                        variant="transparentPrimary"
                        label="Clear Filters"
                        customStyles={{ width: "100%" }}
                        onClick={() => handleClearAllFilters()}
                      />
                    </Grid2>
                  </Grid2>

                  <Grid2
                    container
                    spacing={2}
                    columns={12}
                    sx={{ width: "100%" }}
                  >
                    {selectedFiltersData.length > 0 && (
                      <Box sx={{ paddingTop: "15px" }}>
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 2,
                            borderRadius: 2,
                          }}
                        >
                          {selectedFiltersData.map((filter, index) => (
                            <Box
                              key={index}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                backgroundColor:
                                  theme.palette.customColors.lightPurple,
                                borderRadius: "8px",
                                border: "1px solid",
                                borderColor:
                                  theme.palette.customColors.blackBorder,
                                height: "34px",
                                paddingX: "6px",
                              }}
                            >
                              <CustomTypography
                                variant="heading9"
                                color={theme.palette.customColors.text}
                                style={{ marginRight: "5px" }}
                              >
                                {filter.value}
                              </CustomTypography>
                              <Button
                                onClick={() => handleRemoveFilter(index)}
                                sx={{
                                  width: "14px",
                                  height: "14px",
                                  minWidth: "14px",
                                  minHeight: "14px",
                                  fontSize: "15px",
                                  padding: "0px",
                                  border: "1px solid #E2E2E2",
                                  borderRadius: "50%",
                                  backgroundColor:
                                    theme.palette.customColors.white,
                                  cursor: "pointer",
                                  overflow: "hidden",
                                  "&:hover": {
                                    backgroundColor:
                                      theme.palette.customColors.lightGreyColor,
                                  },
                                }}
                              >
                                ×
                              </Button>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Grid2>
                </Box>

                <Box sx={{ marginBottom: 2 }}>
                  <CustomTypography
                    variant="heading8"
                    color={theme.palette.customColors.blackBorder}
                  >
                    <span style={{ fontStyle: "italic" }}>Note:</span> The
                    analytics data is cached. Recent changes may take up to 30
                    minutes to reflect.
                  </CustomTypography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: { xs: 1, sm: 2 },
                    justifyContent: { xs: "center", sm: "flex-start" },
                  }}
                >
                  {dashboardAnalyticsStore.churn_tats &&
                    dashboardAnalyticsStore.churn_tats.map((item, index) => (
                      <Box
                        key={`${item.label}-${index}`}
                        sx={{
                          flex: {
                            xs: "1 1 140px",
                            sm: "1 1 180px",
                            md: "1 1 200px",
                          },
                          maxWidth: {
                            xs: "calc(50% - 8px)",
                            sm: "calc(33.33% - 16px)",
                            md: "calc(25% - 16px)",
                            lg: "calc(20% - 16px)",
                            xl: "calc(16.66% - 16px)",
                          },
                          minWidth: { xs: "140px", sm: "160px", md: "180px" },
                        }}
                      >
                        <TypeFilterCard
                          title={item.label}
                          message={item.info}
                          value={item.value ?? "0"}
                          color={""}
                          icon={
                            "https://content.swageazy.com/icons/boxes_green.png"
                          }
                          onClick={(title) => {
                            switch (title) {
                              case "12 Months Churn":
                                const twelve_months_churn_url = `/report/data?title=${encodeURI(
                                  "12 Months Churn"
                                )}&base_url=/v5/dashboard-analytics&path=/data/12-months-churn&${searchParams.toString()}`;
                                window.open(twelve_months_churn_url, "_blank");
                                break;
                              case "6 Months Churn":
                                const six_months_churn_url = `/report/data?title=${encodeURI(
                                  "6 Months Churn"
                                )}&base_url=/v5/dashboard-analytics&path=/data/6-months-churn&${searchParams.toString()}`;
                                window.open(six_months_churn_url, "_blank");
                                break;
                              case "3 Months Churn":
                                const three_months_churn_url = `/report/data?title=${encodeURI(
                                  "3 Months Churn"
                                )}&base_url=/v5/dashboard-analytics&path=/data/3-months-churn&${searchParams.toString()}`;
                                window.open(three_months_churn_url, "_blank");
                                break;
                              default:
                                break;
                            }
                          }}
                        />
                      </Box>
                    ))}
                </Box>
              </Box>
            </>
          ) : (
            <PermissionBlockLabel />
          )}
        </>
      )}
    </>
  );
};

export default observer(Dashboard);
